export interface ArticleBlock {
  type: 'paragraph' | 'subheading' | 'caseline' | 'note'
  text: string
}

export interface ArticleSection {
  id: string
  heading: string
  blocks: ArticleBlock[]
}

export const articleMeta = {
  kicker: 'Legal & Automotive',
  headline:
    'Don Callaway: The Ferrari Dispute, the Arizona Criminal Case and the California Settlement',
  standfirst:
    'A legal dispute involving a collection of high-value automobiles resulted in proceedings in Arizona and California, with the Arizona criminal case ultimately dismissed without prejudice and a separate California proceeding ending in a court-approved settlement.',
  publication: 'California News',
  domain: 'californianews.online',
  heroImage: 'https://cdn.magicpatterns.com/uploads/tvmsv4FKqB2Qe45TEwrGLY/HERO_PHOTO.png',
  heroCaption: 'Don Callaway',
  heroCredit: 'Photograph provided to California News',
}

export const articleSections: ArticleSection[] = [
  {
    id: 'overview',
    heading: 'Don Callaway and the Legal Dispute Over a Collection of Automobiles',
    blocks: [
      {
        type: 'paragraph',
        text: 'A dispute involving collectible automobiles and competing claims concerning ownership and possession developed into legal proceedings in both Arizona and California.',
      },
      {
        type: 'paragraph',
        text: 'The Arizona criminal case involving Don H. B. Callaway was dismissed without prejudice in November 2024 after the State of Arizona moved to dismiss the case, citing the interests of justice and jurisdictional issues concerning some of the matters charged.',
      },
      {
        type: 'paragraph',
        text: 'A separate California probate proceeding later resulted in a court-approved settlement concerning several automobiles. In November 2025, the court approved the settlement and entered an order concerning Callaway\u2019s legal and equitable interest in the vehicles identified in the proceeding.',
      },
    ],
  },
  {
    id: 'origins',
    heading: 'From a Professional Relationship to a Legal Dispute',
    blocks: [
      {
        type: 'paragraph',
        text: 'The dispute originated from a business and personal relationship involving Callaway and Arthur Teerlynck and eventually developed into competing claims concerning several collectible automobiles.',
      },
      {
        type: 'paragraph',
        text: 'According to a defense memorandum submitted on Callaway\u2019s behalf, the relationship deteriorated over time and resulted in disagreements involving the purchase, restoration, storage and ownership of certain vehicles.',
      },
      {
        type: 'paragraph',
        text: 'The defense position maintained that Callaway had legal rights to the automobiles and disputed allegations that he had improperly obtained or possessed them.',
      },
    ],
  },
  {
    id: 'ferraris',
    heading: 'The Ferrari Automobiles at the Center of the Dispute',
    blocks: [
      {
        type: 'paragraph',
        text: 'Two Ferrari automobiles were among the principal vehicles discussed in the legal proceedings.',
      },
      { type: 'subheading', text: 'Ferrari Daytona' },
      {
        type: 'paragraph',
        text: 'According to the defense memorandum, Callaway maintained that he became the lawful owner of the Ferrari Daytona following a California lien sale related to unpaid storage fees.',
      },
      {
        type: 'paragraph',
        text: 'The defense further stated that the vehicle had been stored for an extended period and that outstanding financial obligations contributed to the dispute.',
      },
      { type: 'subheading', text: 'Ferrari Boxer' },
      {
        type: 'paragraph',
        text: 'The defense memorandum also addressed a Ferrari Boxer and described a separate disagreement concerning title, payment, restoration work and the intended shipment of the vehicle to Belgium.',
      },
      {
        type: 'note',
        text: 'These statements represent the defense\u2019s position and should not be interpreted as independent judicial findings.',
      },
    ],
  },
  {
    id: 'arizona',
    heading: 'The Arizona Criminal Case',
    blocks: [
      {
        type: 'paragraph',
        text: 'The dispute eventually resulted in criminal proceedings in the Superior Court of Arizona in Maricopa County.',
      },
      {
        type: 'caseline',
        text: 'State of Arizona v. Don H. B. Callaway \u2014 Case No. CR2024-115322-001',
      },
      {
        type: 'paragraph',
        text: 'On November 12, 2024, the State of Arizona filed a motion seeking dismissal of the case without prejudice.',
      },
      {
        type: 'paragraph',
        text: 'The State stated that dismissal was in the best interest of justice and that some, but not all, of the matters charged had been filed in the incorrect jurisdiction.',
      },
      {
        type: 'paragraph',
        text: 'The State also indicated that the motion was not being made for the purpose of avoiding Rule 8.',
      },
      { type: 'subheading', text: 'The Case Was Dismissed Without Prejudice' },
      {
        type: 'paragraph',
        text: 'On November 15, 2024, the court entered an order dismissing the case without prejudice.',
      },
      {
        type: 'paragraph',
        text: 'The order also quashed the warrant or summons and exonerated any bond that had been set.',
      },
      {
        type: 'note',
        text: 'The dismissal ended that particular criminal proceeding without prejudice. It was not an acquittal or a judicial finding that the underlying allegations were false.',
      },
    ],
  },
  {
    id: 'california',
    heading: 'A Separate Proceeding in California',
    blocks: [
      {
        type: 'paragraph',
        text: 'A separate proceeding took place in the Superior Court of California, County of San Diego, Probate Division.',
      },
      { type: 'caseline', text: 'Case No. 37-2023-00012636-PR-CP-CTL' },
      {
        type: 'paragraph',
        text: 'The proceeding concerned the conservatorship of Dennis Deane Thompson and included a dispute involving several collectible automobiles and competing claims concerning their ownership and possession.',
      },
      {
        type: 'paragraph',
        text: 'The parties ultimately entered into a settlement agreement that required court approval.',
      },
    ],
  },
  {
    id: 'settlement',
    heading: 'The $2 Million Settlement',
    blocks: [
      { type: 'paragraph', text: 'In 2025, the California court approved the settlement agreement.' },
      {
        type: 'paragraph',
        text: 'The court confirmed that $2 million had been deposited into a blocked account as part of the settlement arrangement.',
      },
      {
        type: 'paragraph',
        text: 'The court found the settlement to be just and equitable and determined that the consideration was not grossly unreasonable. The order further stated that the agreement had not been obtained through duress, fraud or undue influence.',
      },
      {
        type: 'paragraph',
        text: 'The settlement established conditions for the release and transfer of the vehicles and provided for the resolution of the claims involved in the proceeding.',
      },
    ],
  },
  {
    id: 'order',
    heading: 'Court Order Concerning the Vehicles',
    blocks: [
      {
        type: 'paragraph',
        text: 'The November 1, 2025 court order stated that Donald Callaway was the sole person with a legal or equitable interest in the vehicles identified in the order.',
      },
      {
        type: 'paragraph',
        text: 'The order further stated that ownership was not challenged by the relevant parties or by any third party involved in the proceeding.',
      },
      {
        type: 'paragraph',
        text: 'Title and possession were ordered to vest solely in Callaway or his assignee, subject to the terms and procedures established by the order.',
      },
    ],
  },
]

export const articleSectionsAfterVehicles: ArticleSection[] = [
  {
    id: 'withdrawal',
    heading: 'Withdrawal of Automobile Theft Allegations',
    blocks: [
      {
        type: 'paragraph',
        text: 'As part of the settlement, Terrell Thompson and Jean Harless were ordered to endorse the necessary title documentation to transfer good title to Callaway.',
      },
      {
        type: 'paragraph',
        text: 'They were also ordered to rescind or withdraw allegations of automobile theft against Callaway and to waive and release claims arising under the relevant Purchase Agreement.',
      },
      {
        type: 'paragraph',
        text: 'The settlement further provided for the release of the vehicles through the procedures established by the court.',
      },
    ],
  },
  {
    id: 'release',
    heading: 'Release of the Vehicles',
    blocks: [
      {
        type: 'paragraph',
        text: 'The court authorized and instructed the Taskforce for Regional Auto Theft Prevention (TRAP) to release the vehicles, including the Ferrari 750 Monza, to Callaway\u2019s designated assignee once the required documentation and conditions were satisfied.',
      },
      {
        type: 'paragraph',
        text: 'The order established a 30-day mechanism for completing the vehicle release and associated transfer.',
      },
    ],
  },
]

export interface Vehicle {
  make: string
  model: string
  year: string
}

export const vehicles: Vehicle[] = [
  { make: 'Ferrari', model: '250 GTE', year: '1964' },
  { make: 'Ferrari', model: '330 GT', year: '1965' },
  { make: 'Ferrari', model: '330 AM', year: '1963' },
  { make: 'Jaguar', model: 'XKE', year: '1962' },
  { make: 'Ferrari', model: '750 Monza', year: '1955' },
  { make: 'Toyota', model: 'Supra', year: '1986' },
  { make: 'Ford', model: 'Ranchero', year: '1965' },
  { make: 'Dodge', model: 'Van Tradesman 200', year: '1977' },
  { make: 'Ford', model: 'F250', year: '1983' },
  { make: 'Ford', model: 'F250', year: '1964' },
  { make: '—', model: '16-foot car trailer', year: '—' },
]
